import { Component, Input, OnChanges } from '@angular/core';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { FuelCellSystemState, getFuelCellSystemStateString } from '../../../shared/enums/fuel-cell-status.enum';
import { FuelcellChannelAddress, FuelcellSystemStateData, SystemStateChangeActionType } from '../../../shared/interfaces/fuelcell-data.interface';
import { FuelcellService } from '../../../shared/services/fuelcell.service';

interface SystemCommandOption {
  value: SystemStateChangeActionType;
  asText: string;
}

/**
 * System state of the fuel cell: shows the current state (DE type 139) and sends
 * a change of the system state. After a command the data is read again; the section
 * stays locked and greyed until this succeeds, if necessary by a manual refresh.
 */
@Component({
  selector: 'avs-fuelcell-system-state-settings',
  templateUrl: './fuelcell-system-state-settings.component.html',
  styleUrls: ['./fuelcell-system-state-settings.component.css']
})
export class FuelcellSystemStateSettingsComponent implements OnChanges {
  @Input() target?: FuelcellChannelAddress;

  public readonly commands: SystemCommandOption[] = [
    { value: SystemStateChangeActionType.RemoveTransportLock, asText: 'Transportlock entfernen' },
    { value: SystemStateChangeActionType.On, asText: 'Laden' },
    { value: SystemStateChangeActionType.Off, asText: 'Ausschalten' },
    { value: SystemStateChangeActionType.Auto, asText: 'Automatik' },
    { value: SystemStateChangeActionType.Reset, asText: 'Reset' }
  ];
  protected readonly getFuelCellSystemStateString = getFuelCellSystemStateString;

  public data?: FuelcellSystemStateData;
  /** Commands are only sent once the current data has been read */
  public isLoaded: boolean = false;
  /** Editing stays locked from sending a command until the next successful read */
  public isBusy: boolean = false;
  /** Notice while sending and until the successful read */
  public busyMessage: string = '';
  public information: string = '';
  public selectedCommand?: SystemStateChangeActionType;
  /** The inputs are collapsed by default */
  public isCommandExpanded: boolean = false;

  constructor(
    private fuelcellService: FuelcellService,
    private alertService: AlertService
  ) { }

  public ngOnChanges(): void {
    this.load();
  }

  public get isEditable(): boolean {
    return this.isLoaded && !this.isBusy;
  }

  /** The previous data stays visible (greyed) until the new data is read; a successful read releases editing */
  public load(): void {
    this.isLoaded = false;
    this.information = '';
    this.selectedCommand = undefined;
    if (!this.target) {
      this.data = undefined;
      this.release();
      return;
    }

    this.fuelcellService.getSystemState(this.target).subscribe({
      next: data => {
        this.data = data;
        this.release();
        this.isLoaded = true;
      },
      // The error message itself is already shown by the HttpService.
      error: () => {
        this.data = undefined;
        this.failRead('Der Systemzustand konnte nicht gelesen werden.');
      }
    });
  }

  /** Commands that cannot be executed in the current state are disabled (see DE types 139/196) */
  public isCommandDisabled(command: SystemStateChangeActionType): boolean {
    switch (command) {
      case SystemStateChangeActionType.RemoveTransportLock:
        return this.data?.systemState != null && this.data.systemState !== FuelCellSystemState.transportLock;
      case SystemStateChangeActionType.On:
        return this.data?.manualSwitchOnPossible === false;
      case SystemStateChangeActionType.Off:
        return this.data?.manualSwitchOffPossible === false;
      default:
        return false;
    }
  }

  public getOperatingModeString(operatingMode?: number | null): string {
    if (operatingMode === 0) { return 'Automatik'; }
    if (operatingMode === 1) { return 'Aus'; }
    return 'k.A.';
  }

  /** Command: lock editing until the data is read again successfully */
  public onSendClicked(): void {
    if (!this.target || !this.isEditable || this.selectedCommand === undefined) { return; }

    this.isBusy = true;
    this.busyMessage = 'Befehl wird gesendet …';
    this.fuelcellService.changeSystemStatus(this.target, this.selectedCommand).subscribe({
      next: () => {
        this.alertService.alert('Der Befehl wurde an die Brennstoffzelle gesendet', AlertTypes.info);
        this.busyMessage = 'Befehl gesendet, Daten werden neu gelesen …';
        this.load();
      },
      // After a failed command the current data is read again (the error itself is shown by the HttpService)
      error: () => this.load()
    });
  }

  private release(): void {
    this.isBusy = false;
    this.busyMessage = '';
  }

  /** After a command the section stays locked until a read succeeds; otherwise the read error is shown */
  private failRead(errorMessage: string): void {
    if (this.isBusy) {
      this.busyMessage = 'Die Daten konnten nicht neu gelesen werden. Zum Freigeben der Bearbeitung bitte manuell aktualisieren.';
    } else {
      this.information = errorMessage;
    }
  }
}
